import classNames from "classnames";
import { forwardRef, ReactElement, Ref } from "react";
import { TextArea, TextAreaHandle, TextAreaProps } from "../TextArea/TextArea";
import styles from "./AutogrowTextArea.module.less";

export type AutogrowTextAreaHandle = TextAreaHandle;

export type AutogrowTextAreaProps = Omit<TextAreaProps, "height" | "isResizable" | "rows"> & {
	hasNoMinHeight?: boolean;
};

export const AutogrowTextArea = forwardRef((props: AutogrowTextAreaProps, ref: Ref<AutogrowTextAreaHandle>): ReactElement => {
	const {
		hasNoMinHeight = false,
		...textAreaProps
	} = props;

	const {
		value,
		hasNoPadding,
		isBorderless
	} = textAreaProps;

	return (
		<div className={styles.root}>
			<div className={styles.inputWrapper}>
				<TextArea
					{...textAreaProps}
					height="fill"
					ref={ref}
					// The TextArea and mirror can be briefly out of sync due to async re-rendering.
					// During that moment, the TextArea can show a vertical scrollbar because its height is
					// tied to the mirror’s height (which is temporarily too small).
					// Once shown, the scrollbar consumes horizontal space, causing additional wrapping in the
					// TextArea and effectively adding an extra line compared to the mirror.
					// This “extra wrap” keeps the height desync alive until the scrollbar is removed.
					verticalOverflow="hidden"
				/>
			</div>
			<div className={classNames(
				styles.mirror,
				{
					[styles.mirror_hasNoPadding]: hasNoPadding,
					[styles.mirror_isBorderless]: isBorderless,
					[styles.mirror_hasNoMinHeight]: hasNoMinHeight
				}
			)}>
				{getMirrorContent(value)}
			</div>
		</div>
	);
});

AutogrowTextArea.displayName = "AutogrowTextArea";

function getMirrorContent(value: string | null): string {
	if (!value) {
		return "\u200B";
	}

	// The mirror div with white-space: pre-wrap renders trailing newlines differently
	// than the textarea element, causing a 1 line height discrepancy. Appending
	// a zero-width space after trailing newlines ensures the mirror height matches
	// the textarea height.
	if (value.endsWith("\r\n") || value.endsWith("\n")) {
		return `${value}\u200B`;
	}

	return value;
}